import CardHeader from "components/CardHeader";
import { getProjectProgress, ProjectProgressBar } from "components/ProjectTimeline";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Asset } from "models/Asset";
import React from "react";
import { Card, CardBody } from "reactstrap";
import "./asset-lifecycle-timeline.scss";

type AssetLifecycle = Pick<Asset,
    "purchase_date" | "installation_date" | "warranty_until" | "end_of_life_date"
>;

export const getAssetLifecycleProgress = (asset: AssetLifecycle, now = Date.now()): number | null => {
    if (!asset.purchase_date || !asset.end_of_life_date) return null;

    const purchase = new Date(asset.purchase_date).getTime();
    const endOfLife = new Date(asset.end_of_life_date).getTime();
    if (!Number.isFinite(purchase) || !Number.isFinite(endOfLife) || endOfLife <= purchase) return null;

    return getProjectProgress({
        start_date: asset.purchase_date,
        end_date: asset.end_of_life_date,
    }, now);
};

interface Props {
    asset: AssetLifecycle;
}

const AssetLifecycleTimeline: React.FC<Props> = ({asset}) => {
    const {t, intlLocale} = useOfficeTranslation();
    const progress = getAssetLifecycleProgress(asset);
    const milestones = [
        {label: t("assets.form.purchaseDate"), value: asset.purchase_date},
        {label: t("assets.form.installationDate"), value: asset.installation_date},
        {label: t("assets.form.warrantyUntil"), value: asset.warranty_until},
        {label: t("assets.form.endOfLifeDate"), value: asset.end_of_life_date},
    ];
    const formatDate = (value?: string) => {
        if (!value) return "—";
        try {
            return new Intl.DateTimeFormat(intlLocale, {dateStyle: "medium"}).format(new Date(value));
        } catch {
            return "—";
        }
    };

    return <Card className="asset-lifecycle-card mb-4">
        <CardBody>
            <CardHeader
                title={t("assets.detail.lifecycle")}
                icon="mdi-timeline-clock-outline"
                actions={progress !== null && <span className="text-muted">{Math.round(progress)}%</span>}
            />
            <div className="asset-lifecycle pt-4">
                {progress === null
                    ? <div className="project-timeline asset-lifecycle__empty-track" aria-hidden="true" />
                    : <ProjectProgressBar
                        ariaLabel={t("assets.detail.lifecycleProgress", {percentage: Math.round(progress)})}
                        progress={progress}
                    />}
                <dl className="asset-lifecycle__dates">
                    {milestones.map(milestone => <div className="asset-lifecycle__date" key={milestone.label}>
                        <dt>{milestone.label}</dt>
                        <dd>{formatDate(milestone.value)}</dd>
                    </div>)}
                </dl>
            </div>
        </CardBody>
    </Card>;
};

export default AssetLifecycleTimeline;
